/** Canonical asset ids shared by every Lunaria write boundary. */
const CONTENT_TYPE_EXTENSIONS = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "audio/mpeg": "mp3",
    "audio/wav": "wav",
    "audio/x-wav": "wav",
    "audio/ogg": "ogg",
    "audio/mp4": "m4a",
    "video/mp4": "mp4",
    "video/webm": "webm",
};
const EXTENSION_ALIASES = {
    jpeg: "jpg",
    jpe: "jpg",
    wave: "wav",
};
const MAX_BASENAME_BYTES = 255;
function shortHash(value) {
    let current = 0x811c9dc5;
    for (let index = 0; index < value.length; index += 1) {
        current ^= value.charCodeAt(index);
        current = Math.imul(current, 0x01000193);
    }
    return (current >>> 0).toString(36);
}
function normalizedStem(value, fallback = "asset", preserveSections = false) {
    const prepared = value
        .normalize("NFKD")
        .toLocaleLowerCase("en-US")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\/:\u0000-\u001f\u007f]+/g, "_")
        .replace(/[^a-z0-9_-]+/g, "_");
    const normalized = (preserveSections ? prepared.replace(/_{3,}/g, "__") : prepared.replace(/_+/g, "_"))
        .replace(/-{3,}/g, "--")
        .replace(/^[-_.]+|[-_.]+$/g, "");
    return normalized || `${fallback}_${shortHash(value.normalize("NFKC"))}`;
}
function utf8ByteLength(value) {
    return new TextEncoder().encode(value).byteLength;
}
function truncateUtf8(value, maxBytes) {
    let result = "";
    let usedBytes = 0;
    for (const character of value) {
        const characterBytes = utf8ByteLength(character);
        if (usedBytes + characterBytes > maxBytes)
            break;
        result += character;
        usedBytes += characterBytes;
    }
    return result;
}
function truncateSchemeStem(stem, maxBytes) {
    const sections = stem.split("__");
    if ((sections.length !== 3 && sections.length !== 4) || sections.some((section) => !section)) {
        return truncateUtf8(stem, maxBytes);
    }
    const separatorBytes = (sections.length - 1) * 2;
    const contentBudget = maxBytes - separatorBytes;
    if (contentBudget < sections.length)
        return truncateUtf8(stem, maxBytes);
    const sectionBytes = sections.map(utf8ByteLength);
    const evenShare = Math.floor(contentBudget / sections.length);
    const quotas = sectionBytes.map((bytes, index) => (Math.min(bytes, evenShare + (index < contentBudget % sections.length ? 1 : 0))));
    let remaining = contentBudget - quotas.reduce((sum, quota) => sum + quota, 0);
    while (remaining > 0) {
        let distributed = false;
        for (let index = 0; index < quotas.length && remaining > 0; index += 1) {
            if ((sectionBytes[index] ?? 0) <= (quotas[index] ?? 0))
                continue;
            quotas[index] = (quotas[index] ?? 0) + 1;
            remaining -= 1;
            distributed = true;
        }
        if (!distributed)
            break;
    }
    return sections.map((section, index) => truncateUtf8(section, quotas[index] ?? 0)).join("__");
}
function finalizeAssetId(stem, extension) {
    const extensionPart = `.${extension}`;
    const unbounded = `${stem}${extensionPart}`;
    if (utf8ByteLength(unbounded) <= MAX_BASENAME_BYTES)
        return unbounded;
    const hashSuffix = `--${shortHash(unbounded)}`;
    const stemBudget = MAX_BASENAME_BYTES - utf8ByteLength(extensionPart) - utf8ByteLength(hashSuffix);
    return `${truncateSchemeStem(stem, stemBudget)}${hashSuffix}${extensionPart}`;
}
function sourceFilename(value) {
    return value.trim().replace(/\\/g, "/").split("/").filter(Boolean).at(-1) ?? "";
}
function splitSourceName(sourceName) {
    const filename = sourceFilename(sourceName ?? "");
    const match = /^(.*?)(?:\.([a-z0-9]{1,8}))?$/i.exec(filename);
    return {
        stem: match?.[1] ?? filename,
        ...(match?.[2] ? { extension: match[2].toLowerCase() } : {}),
    };
}
function extensionFor(sourceName, contentType) {
    const normalizedType = contentType?.split(";", 1)[0]?.trim().toLowerCase();
    const fromType = normalizedType ? CONTENT_TYPE_EXTENSIONS[normalizedType] : undefined;
    const fromName = splitSourceName(sourceName).extension;
    const extension = fromType ?? fromName ?? "png";
    return EXTENSION_ALIASES[extension] ?? extension;
}
/** IDE-compatible Scheme B sprite stem: `<character>__<outfit>__<pose>`. */
export function canonicalSpriteStem(characterId, look) {
    const character = normalizedStem(characterId, "character");
    const sections = look.normalize("NFKC").toLocaleLowerCase("en-US").split("__");
    if ((sections.length === 3 || sections.length === 4)
        && normalizedStem(sections[0] ?? "") === character
        && sections.every((section) => /[a-z0-9]/i.test(normalizedStem(section)))) {
        return sections.map((section) => normalizedStem(section)).join("__");
    }
    return `${character}__default__${normalizedStem(look, "neutral")}`;
}
/** Canonical id for a semantic target (upload, generation, or cutout). */
export function canonicalAssetId(target) {
    const stem = target.kind === "character"
        ? canonicalSpriteStem(target.characterId ?? "character", target.symbol)
        : normalizedStem(target.symbol);
    return finalizeAssetId(stem, extensionFor(target.sourceName, target.contentType));
}
/** Canonical id for an unbound/direct upload where no semantic target exists. */
export function canonicalLooseAssetId(sourceName, contentType) {
    const source = splitSourceName(sourceName);
    const canonicalSections = /^.+__.+__.+(?:__.+)?$/i.test(source.stem);
    const normalized = normalizedStem(source.stem, "asset", canonicalSections);
    const extension = extensionFor(sourceName, contentType);
    const unboundedCanonical = `${normalized}.${extension}`;
    const originalBasename = sourceFilename(sourceName).normalize("NFKC");
    const fallbackAlreadyDisambiguates = normalized === `asset_${shortHash(source.stem.normalize("NFKC"))}`;
    const collisionSafeStem = originalBasename === unboundedCanonical || fallbackAlreadyDisambiguates
        ? normalized
        : `${normalized}--${shortHash(originalBasename)}`;
    return finalizeAssetId(collisionSafeStem, extension);
}
